import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Source, Layer } from 'react-map-gl';
import { getComprehensiveTractData } from '../services/overlays';

const ComprehensiveTractLayer = ({ visible, onTractClick, dataLayer = 'income', viewport, mapRef, onLoadingChange }) => {
  const [tractData, setTractData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastFetchRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  // Notify parent of loading state changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(loading);
    }
  }, [loading, onLoadingChange]);

  const MIN_ZOOM = 10; // Only show tracts when zoomed in enough
  const DEBOUNCE_MS = 1000; // Wait 1 second after viewport stops changing

  // Calculate bounding box from viewport or map bounds
  const getBoundingBox = useCallback((vp) => {
    if (!vp || !vp.longitude || !vp.latitude || !vp.zoom) return null;

    // Try to get actual bounds from map if available
    if (mapRef && mapRef.current) {
      try {
        const map = mapRef.current.getMap();
        if (map && map.getBounds) {
          const bounds = map.getBounds();
          return {
            minLng: bounds.getWest(),
            maxLng: bounds.getEast(),
            minLat: bounds.getSouth(),
            maxLat: bounds.getNorth()
          };
        }
      } catch (err) {
        console.warn('Could not get map bounds, using viewport calculation');
      }
    }

    // Fallback: Calculate approximate bounding box based on viewport
    const lat = vp.latitude;
    const lng = vp.longitude;
    const zoom = vp.zoom;

    // More accurate calculation using Mercator projection
    const n = Math.pow(2, zoom);
    const latRad = lat * Math.PI / 180;
    const latPerPixel = 360 / (256 * n);
    const lngPerPixel = 360 / (256 * n * Math.cos(latRad));

    // Approximate viewport size (assuming typical map container size)
    // These will be adjusted based on actual map size
    const mapWidth = 1200; // Approximate pixels
    const mapHeight = 800; // Approximate pixels

    const latDelta = (mapHeight / 2) * latPerPixel;
    const lngDelta = (mapWidth / 2) * lngPerPixel;

    return {
      minLng: lng - lngDelta,
      maxLng: lng + lngDelta,
      minLat: lat - latDelta,
      maxLat: lat + latDelta
    };
  }, [mapRef]);

  const fetchTractData = useCallback(async (bbox) => {
    try {
      setLoading(true);
      setError(null);
      console.log('🗺️ Fetching comprehensive tract data for bbox:', bbox);

      const params = new URLSearchParams({
        minLng: bbox.minLng.toFixed(6),
        maxLng: bbox.maxLng.toFixed(6),
        minLat: bbox.minLat.toFixed(6),
        maxLat: bbox.maxLat.toFixed(6)
      });


      const data = await getComprehensiveTractData(params);

      console.log(`✅ Comprehensive tract data loaded: ${data.features?.length || 0} tracts`);

      if (data && data.features && data.features.length > 0) {
        setTractData(data);
        lastFetchRef.current = { bbox, data };
      } else {
        // No data for this area, but that's okay
        setTractData({ type: 'FeatureCollection', features: [] });
      }

    } catch (error) {
      console.error('❌ Error fetching comprehensive tract data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch data when viewport changes (debounced)
  useEffect(() => {
    if (!visible || !viewport || viewport.zoom < MIN_ZOOM) {
      setTractData(null);
      return;
    }

    const bbox = getBoundingBox(viewport);
    if (!bbox) return;

    // Check if we already have data for this area
    if (lastFetchRef.current) {
      const lastBbox = lastFetchRef.current.bbox;
      const bboxOverlap =
        bbox.minLng <= lastBbox.maxLng &&
        bbox.maxLng >= lastBbox.minLng &&
        bbox.minLat <= lastBbox.maxLat &&
        bbox.maxLat >= lastBbox.minLat;

      // If new bbox is mostly contained in old bbox, keep existing data
      const contained =
        bbox.minLng >= lastBbox.minLng &&
        bbox.maxLng <= lastBbox.maxLng &&
        bbox.minLat >= lastBbox.minLat &&
        bbox.maxLat <= lastBbox.maxLat;

      if (contained && lastFetchRef.current.data) {
        // Use existing data if it covers the current view
        return;
      }
    }

    // Debounce the fetch
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      fetchTractData(bbox);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [visible, viewport, dataLayer, getBoundingBox, fetchTractData]);

  // Get color based on data layer type
  const getColorExpression = (layerType) => {
    // Read tract MFI as % of MSA/MD MFI from `mfi_pct`
    //  0      → unknown (grey)
    //  >0–50  → low (red)
    //  50–80  → moderate (orange)
    //  80–120 → middle (yellow-green)
    //  ≥120   → upper (green)
    return [
      'case',

      // 0: unknown / not available
      ['==', ['get', 'incomePercent'], 0],
      '#9CA3AF',                // grey

      // 1: low  (0% < pct < 50%)
      ['all',
        ['>', ['get', 'incomePercent'], 0],
        ['<', ['get', 'incomePercent'], 50]
      ],
      '#DC2626',                // red

      // 2: moderate (50% ≤ pct < 80%)
      ['all',
        ['>=', ['get', 'incomePercent'], 50],
        ['<', ['get', 'incomePercent'], 80]
      ],
      '#F97316',                // orange

      // 3: middle (80% ≤ pct < 120%)
      ['all',
        ['>=', ['get', 'incomePercent'], 80],
        ['<', ['get', 'incomePercent'], 120]
      ],
      '#A3E635',                // yellow-green

      // 4: upper (pct ≥ 120%)
      '#16A34A'                 // green
    ];
  };

  // Don't show if not visible, zoomed out too far, or no viewport
  if (!visible || !viewport || viewport.zoom < MIN_ZOOM) {
    return null;
  }

  if (loading && !tractData) {
    return null; // Don't show anything while loading initial data
  }

  if (error) {
    console.warn('⚠️ Tract layer error:', error);
    return null;
  }

  if (!tractData || !tractData.features || tractData.features.length === 0) {
    return null;
  }

  // Determine if we have polygon geometry or point geometry
  const hasPolygons = tractData.features.some(f =>
    f.geometry && (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon')
  );

  const sourceId = "comprehensive-tracts";

  // Render layers based on geometry type
  const renderLayers = () => {
    if (hasPolygons) {
      return [
        // Fill layer for polygons - similar to FFIEC map
        <Layer
          key="tract-fill"
          id="tract-fill"
          type="fill"
          paint={{
            'fill-color': getColorExpression(dataLayer),
            'fill-opacity': 0.6,
            'fill-outline-color': '#2563eb' // Blue outline like FFIEC
          }}
          interactive={true}
        />,
        // Border layer - thicker outline for visibility
        <Layer
          key="tract-border"
          id="tract-border"
          type="line"
          paint={{
            'line-color': '#2563eb', // Blue border like FFIEC
            'line-width': [
              'interpolate',
              ['linear'],
              ['zoom'],
              10, 0.5,
              12, 1,
              14, 1.5
            ],
            'line-opacity': 0.9
          }}
        />,
        // Label layer for tract codes - styled like FFIEC
        <Layer
          key="tract-labels"
          id="tract-labels"
          type="symbol"
          layout={{
            'text-field': [
              'coalesce',
              ['get', 'tractCode'],
              ['get', 'TRACTCE'],
              ['get', 'NAME']
            ],
            'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
            'text-size': [
              'interpolate',
              ['linear'],
              ['zoom'],
              10, 10,
              12, 11,
              14, 12
            ],
            'text-anchor': 'center',
            'text-allow-overlap': false,
            'text-ignore-placement': false
          }}
          paint={{
            'text-color': '#1e293b', // Dark blue-gray
            'text-halo-color': '#ffffff',
            'text-halo-width': 2,
            'text-halo-blur': 1
          }}
          minzoom={10}
        />
      ];
    } else {
      // Point features fallback
      return (
        <Layer
          id="tract-points"
          type="circle"
          paint={{
            'circle-radius': [
              'interpolate',
              ['linear'],
              ['get', 'population'],
              0, 4,
              1000, 6,
              5000, 10,
              10000, 14
            ],
            'circle-color': getColorExpression(dataLayer),
            'circle-opacity': 0.7,
            'circle-stroke-width': 1,
            'circle-stroke-color': '#ffffff'
          }}
          interactive={true}
        />
      );
    }
  };

  return (
    <Source id={sourceId} type="geojson" data={tractData}>
      {renderLayers()}
    </Source>
  );
};

export default ComprehensiveTractLayer;

